import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Search, Tags, Package, Barcode } from "lucide-react";
import { api } from "@/lib/api";
import { matches } from "@/lib/search";
import { brl } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Badge, Button, Chip, EmptyState, ErrorState, Input, Loading, Page, PageHeader } from "@/components/kit";

export default function Products() {
  const nav = useNavigate();
  const { isMgmt } = useAuth();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const { data = [], isLoading, isError, error, refetch } = useQuery({ queryKey: ["products"], queryFn: () => api("/products") });
  const { data: cats = [] } = useQuery({ queryKey: ["categories"], queryFn: () => api("/categories") });
  const list = useMemo(() => data.filter((p) => (cat === "all" || p.category_id === cat) && matches(p.name, q)), [data, q, cat]);
  return (
    <Page>
      <PageHeader title="Produtos" subtitle={`${data.length} insumos cadastrados`}
        right={isMgmt && (
          <div className="flex gap-2">
            <Button variant="secondary" icon={Tags} onClick={() => nav("/categories")} data-testid="manage-categories-button"><span className="hidden sm:inline">Categorias</span></Button>
            <Button variant="secondary" icon={Barcode} onClick={() => nav("/pdv-codes")} data-testid="import-pdv-codes-button"><span className="hidden sm:inline">Códigos PDV</span></Button>
            <Button icon={Plus} onClick={() => nav("/products/new")} data-testid="add-product-button"><span className="hidden sm:inline">Novo produto</span></Button>
          </div>
        )} />
      <div className="relative mb-3">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 muted" />
        <Input className="pl-10" placeholder="Buscar produto" value={q} onChange={(e) => setQ(e.target.value)} data-testid="product-search-input" />
      </div>
      <div className="scroll-x mb-5">
        <Chip active={cat === "all"} onClick={() => setCat("all")} data-testid="category-chip-all">Todas</Chip>
        {cats.map((c) => <Chip key={c.id} active={cat === c.id} onClick={() => setCat(c.id)} data-testid={`category-chip-${c.id}`}>{c.name}</Chip>)}
      </div>
      {isLoading ? <Loading /> : isError ? <ErrorState message={error.message} onRetry={refetch} /> : !data.length ? (
        <EmptyState title="Nenhum produto" text="Cadastre categorias e depois adicione seus insumos com o custo unitário." />
      ) : (
        <div className="card divide" style={{ padding: 0 }} data-testid="products-list">
          {list.map((p) => (
            <div key={p.id} className={`row-link ${isMgmt ? "" : "cursor-default"}`} onClick={() => isMgmt && nav(`/products/${p.id}`)} data-testid={`product-row-${p.id}`}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--brand-soft)" }}><Package size={17} style={{ color: "var(--brand)" }} /></div>
              <div className="flex-1 min-w-0">
                <div className="h3 truncate">{p.name}</div>
                <div className="caption truncate">{p.category_name || "Sem categoria"}{p.sector_name ? ` · ${p.sector_name}` : ""}{p.sales_code ? ` · PDV ${p.sales_code}` : ""}</div>
              </div>
              {!p.active && <Badge tone="muted">Inativo</Badge>}
              <div className="text-right"><div className="label tnum">{brl(p.unit_cost)}</div><div className="caption">por {p.unit}</div></div>
            </div>
          ))}
          {!list.length && <p className="caption p-5">Nenhum produto encontrado.</p>}
        </div>
      )}
    </Page>
  );
}
